import { Injectable, signal, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';
import { API_BASE } from '../../constants/general.constants';
import { NotificationWebsocketService } from '../notification-websocket/notification-websocket.service';
import { RTMessagingClient } from '../asyncapi-websocket/rt-messaging-client';
import { WebSocketClientState } from '../asyncapi-websocket/base-asyncapi-client';
import {
  EntryInfo,
  SubscriptionInfo,
  ChannelWithEvents,
  GetEntriesParams,
  DeliveryMethod,
} from '../../interfaces/rt-messaging.generated';

export interface SubscriptionUpdate {
  deliveryMethods: DeliveryMethod[];
}

export interface SubscriptionCreate {
  eventId: number;
  projectId?: number;
  deliveryMethods?: DeliveryMethod[];
}

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private readonly http = inject(HttpClient);
  private readonly wsService = inject(NotificationWebsocketService);

  private readonly subscriptionsEndpoint = `${API_BASE}/users/current/subscriptions`;
  private readonly destroy$ = new Subject<void>();

  /** All loaded entries */
  readonly entries = signal<EntryInfo[]>([]);

  /** Unseen count */
  readonly unseenCount = signal<number>(0);

  /** Whether entries are currently loading */
  readonly loading = signal<boolean>(false);

  constructor() {
    this.listenForNewEntries();
    this.refreshOnConnect();
  }

  /**
   * Access the underlying RTMessagingClient via the WebSocket service.
   * Returns null if not connected.
   */
  private get client(): RTMessagingClient | null {
    return (this.wsService as any).client as RTMessagingClient | null;
  }

  /**
   * Listen for real-time new entries from WebSocket push notifications
   */
  private listenForNewEntries(): void {
    this.wsService.receiveMessage()
      .pipe(takeUntil(this.destroy$))
      .subscribe((msg: any) => {
        if (msg.message === 'new-entries' && Array.isArray(msg.entries)) {
          const newEntries = msg.entries as EntryInfo[];
          // Prepend new entries to existing list
          this.entries.update(current => [...newEntries, ...current]);
          // Update unseen count
          const newUnseen = newEntries.filter(e => !e.entrySeen).length;
          if (newUnseen > 0) {
            this.unseenCount.update(count => count + newUnseen);
          }
        }
      });
  }

  /**
   * Automatically refresh unseen count when WebSocket connects
   */
  private refreshOnConnect(): void {
    this.wsService.state$
      .pipe(
        filter(state => state === WebSocketClientState.CONNECTED),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.refreshUnseenCount();
      });
  }

  /**
   * Load notification entries via WebSocket
   */
  async loadEntries(params?: GetEntriesParams): Promise<EntryInfo[]> {
    if (!this.client) return [];
    this.loading.set(true);
    try {
      const entries = await this.client.getEntries(params);
      this.entries.set(entries);
      return entries;
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Refresh the unseen count
   */
  async refreshUnseenCount(): Promise<void> {
    if (!this.client) return;
    try {
      const unseen = await this.client.getEntries({ seen: false });
      this.unseenCount.set(unseen.length);
    } catch {
      // Silently fail - unseen count is a nice-to-have
    }
  }

  /**
   * Mark unseen entries as seen.
   * When channelId is provided, only marks entries for that channel;
   * otherwise marks all unseen entries.
   */
  async markAllSeen(channelId?: number | null): Promise<void> {
    if (!this.client) return;
    const params: GetEntriesParams = { seen: false };
    if (channelId != null) {
      params.channelId = channelId;
    }
    const unseen = await this.client.getEntries(params);
    if (unseen.length === 0) return;
    const ids = unseen.map(e => e.entryId);
    await this.client.markSeen(ids);
    // Update local state
    this.entries.update(entries =>
      entries.map(e =>
        ids.includes(e.entryId) ? { ...e, entrySeen: true } : e
      )
    );
    if (channelId != null) {
      this.unseenCount.update(count => Math.max(0, count - ids.length));
    } else {
      this.unseenCount.set(0);
    }
  }

  /**
   * Mark specific entries as seen
   */
  async markSeen(entryIds: number[]): Promise<void> {
    if (!this.client || entryIds.length === 0) return;
    await this.client.markSeen(entryIds);
    // Update local state
    this.entries.update(entries =>
      entries.map(e =>
        entryIds.includes(e.entryId) ? { ...e, entrySeen: true } : e
      )
    );
    this.unseenCount.update(count => Math.max(0, count - entryIds.length));
  }

  /**
   * List all channels with their events via WebSocket
   */
  async listChannels(): Promise<ChannelWithEvents[]> {
    if (!this.client) return [];
    return this.client.listChannels();
  }

  /**
   * List current subscriptions via WebSocket
   */
  async listSubscriptions(): Promise<SubscriptionInfo[]> {
    if (!this.client) return [];
    return this.client.listSubscriptions();
  }

  // ==================== REST API Methods ====================

  /**
   * Get subscriptions via REST
   */
  getSubscriptions(): Observable<SubscriptionInfo[]> {
    return this.http.get<SubscriptionInfo[]>(this.subscriptionsEndpoint);
  }

  /**
   * Update delivery methods for a subscription
   */
  updateDeliveryMethods(subId: number, methods: DeliveryMethod[]): Observable<SubscriptionInfo> {
    return this.http.put<SubscriptionInfo>(
      `${this.subscriptionsEndpoint}/${subId}/delivery-methods`,
      { deliveryMethods: methods }
    );
  }

  /**
   * Create a new subscription
   */
  createSubscription(data: SubscriptionCreate): Observable<SubscriptionInfo> {
    return this.http.post<SubscriptionInfo>(this.subscriptionsEndpoint, data);
  }

  /**
   * Delete a subscription
   */
  deleteSubscription(subId: number): Observable<void> {
    return this.http.delete<void>(`${this.subscriptionsEndpoint}/${subId}`);
  }

  dispose(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
